import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { toast } from "sonner"
import { loginUser, organizationLogin } from "../../services/authService";
import apiService from "../../services/api.services";
import { safeValidate, AuthResponseSchema } from "../../schemas/api.schemas";
import {
  AuthState,
  LoginCredentials,
  User,
  RegisterCredentials,
  AuthOfflineQueueItem,
  AuthResponse,
} from "./authTypes";

// Constants
const AUTH_STORAGE_KEY = "tredot_auth_data";
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

// Helper functions
export const saveToStorage = (data: {
  user: User;
  tredot_accessToken: string;
  tredot_refreshToken: string;
}) => {
  try {
    const authData = {
      ...data,
      accessToken: data.tredot_accessToken,
      refreshToken: data.tredot_refreshToken,
    };
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
  } catch (error) {
    console.error("Failed to save auth data to storage:", error);
  }
};

const loadFromStorage = () => {
  try {
    const data = localStorage.getItem(AUTH_STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Failed to load auth data from storage:", error);
    return null;
  }
};

const clearStorage = () => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
};

// Initial state
const storedAuth = loadFromStorage();
const initialState: AuthState = {
  user: storedAuth?.user || null,
  status: "idle",
  error: null,
  isAuthenticated: !!(storedAuth?.accessToken || storedAuth?.tredot_accessToken),
  offlineQueue: [],
};

// Thunks
export const login = createAsyncThunk<
  AuthResponse,
  LoginCredentials,
  { rejectValue: { message: string } }
>("auth/login", async (credentials, { rejectWithValue }) => {
  let retryCount = 0;

  while (retryCount < MAX_RETRIES) {
    try {
      const rawResponse = await loginUser(credentials);
      const response = safeValidate(AuthResponseSchema, rawResponse, "login") as AuthResponse;
      // Save auth data to storage
      saveToStorage({
        user: response.user,
        tredot_accessToken: response.user.accessToken,
        tredot_refreshToken: response.user.refreshToken,
      });
      // Set default authorization header
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${response.user.accessToken}`;

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const status = axiosError.response?.status;

      // Don't retry client-side errors (4xx) — surface the message immediately
      if (status && status >= 400 && status < 500) {
        return rejectWithValue({
          message: axiosError.response?.data?.message || "Login failed",
        });
      }

      retryCount++;
      if (retryCount === MAX_RETRIES) {
        return rejectWithValue({
          message: axiosError.response?.data?.message || "Login failed",
        });
      }

      await new Promise((resolve) =>
        setTimeout(resolve, RETRY_DELAY * retryCount)
      );
    }
  }

  return rejectWithValue({ message: "Max retries exceeded" });
});

export const loginOrganization = createAsyncThunk<
  AuthResponse,
  LoginCredentials,
  { rejectValue: { message: string } }
>("auth/loginOrganization", async (credentials, { rejectWithValue }) => {
  let retryCount = 0;
  while (retryCount < MAX_RETRIES) {
    try {
      const response = await organizationLogin(credentials);
      // Save auth data to storage
      saveToStorage({
        user: response.user,
        tredot_accessToken: response.user.accessToken,
        tredot_refreshToken: response.user.refreshToken,
      });
      // Set default authorization header
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${response.user?.accessToken}`;
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const status = axiosError.response?.status;

      // Don't retry client-side errors (4xx) — surface the message immediately
      if (status && status >= 400 && status < 500) {
        return rejectWithValue({
          message: axiosError.response?.data?.message || "Login failed",
        });
      }

      retryCount++;
      if (retryCount === MAX_RETRIES) {
        return rejectWithValue({
          message: axiosError.response?.data?.message || "Login failed",
        });
      }

      await new Promise((resolve) =>
        setTimeout(resolve, RETRY_DELAY * retryCount)
      );
    }
  }

  return rejectWithValue({ message: "Max retries exceeded" });
});

export const registerTutor = createAsyncThunk<
  AuthResponse,
  RegisterCredentials,
  { rejectValue: { message: string } }
>("auth/register-tutor", async (credentials, { rejectWithValue }) => {
  let retryCount = 0;

  while (retryCount < MAX_RETRIES) {
    try {
      const response = await axios.post<AuthResponse>(
        `/auth/register`,
        credentials,
        { withCredentials: true }
      );
      // Save auth data to storage
      await apiService.authService.setTokens({
        accessToken: response.data.user.accessToken,
        refreshToken: response.data.user.refreshToken,
      }, response.data.user);

      saveToStorage({
        user: response.data.user,
        tredot_accessToken: response.data?.user.accessToken,
        tredot_refreshToken: response.data?.user.refreshToken,
      });

      // Set default authorization header
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${response.data.user?.accessToken}`;

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      retryCount++;

      if (retryCount === MAX_RETRIES) {

        return rejectWithValue({
          message: axiosError.response?.data?.message || "Registration failed",
        });
      }

      await new Promise((resolve) =>
        setTimeout(resolve, RETRY_DELAY * retryCount)
      );
    }
  }

  return rejectWithValue({ message: "Max retries exceeded" });
});

export const registerStudent = createAsyncThunk<
  AuthResponse,
  any,
  { rejectValue: { message: string } }
>(
  "auth/register-student",
  async (credentials, { rejectWithValue }) => {
    let retryCount = 0;
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // 1 second

    while (retryCount < MAX_RETRIES) {
      try {
        const response = await apiService.post<AuthResponse>(
          `/auth/register-student`,
          credentials
        );

        // Save auth data
        await apiService.authService.setTokens({
          accessToken: response.user.accessToken,
          refreshToken: response.user.refreshToken,
        }, response.user);

        saveToStorage({
          user: response.user,
          tredot_accessToken: response.user.accessToken,
          tredot_refreshToken: response.user.refreshToken,
        });

        // Set default auth header
        axios.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${response.user.accessToken}`;

        return response;
      } catch (error) {
        const axiosError = error as AxiosError<{ message: string }>;
        console.error(`Attempt ${retryCount + 1} failed:`, axiosError.message);

        retryCount++;
        if (retryCount === MAX_RETRIES) {
          return rejectWithValue({
            message: axiosError.response?.data?.message || "Registration failed",
          });
        }

        // Exponential backoff
        const delay = RETRY_DELAY * Math.pow(2, retryCount);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
    return rejectWithValue({ message: "Max retries exceeded" });
  }
);


export const logout = createAsyncThunk<
  null,
  void,
  { rejectValue: { message: string } }
>("auth/logout", async (_, { rejectWithValue }) => {
  try {
    await apiService.post(`/auth/logout`, {});
    // Clear stored data
    clearStorage();
    // Remove authorization header
    delete axios.defaults.headers.common["Authorization"];

    return null;
  } catch (error) {
    const axiosError = error as AxiosError<{ message: string }>;
    // toast.error(axiosError.response?.data?.message || "Logout failed");
    return rejectWithValue({
      message: axiosError.response?.data?.message || "Logout failed",
    });
  }
});

// Slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetAuthStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },

    resetAuthData: (state) => {
      state.status = "idle";
      state.error = null;
      state.user = null;
      state.isAuthenticated = false;
    },

    addToOfflineQueue: (state, action: PayloadAction<AuthOfflineQueueItem>) => {
      state.offlineQueue.push(action.payload);
    },
    removeFromOfflineQueue: (
      state,
      action: PayloadAction<AuthOfflineQueueItem>
    ) => {
      state.offlineQueue = state.offlineQueue.filter(
        (item) => item.timestamp !== action.payload.timestamp
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "succeeded";
        const userData = action.payload.user;
        state.user = {
          ...userData,
          accessToken: userData.accessToken,
          refreshToken: userData.refreshToken,
        };
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Login failed";
        state.isAuthenticated = false;
      })
      // organization login cases
      .addCase(loginOrganization.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loginOrganization.fulfilled, (state, action) => {
        state.status = "succeeded";
        const organizationData = action.payload.user;
        state.user = {
          ...organizationData,
          accessToken: organizationData.accessToken,
          refreshToken: organizationData.refreshToken,
        };
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginOrganization.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Login failed";
        state.isAuthenticated = false;
      })
      // Register tutor cases
      .addCase(registerTutor.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(registerTutor.fulfilled, (state, action) => {
        state.status = "succeeded";
        const tutorData = action.payload.user;
        state.user = {
          ...tutorData,
          accessToken: action.payload.accessToken,
          refreshToken: action.payload.refreshToken,
        };
        state.isAuthenticated = true;
        state.error = null;
        toast.success("Registered successfully");
      })
      .addCase(registerTutor.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Registration failed";
        state.isAuthenticated = false;
      })

      // Logout cases
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.status = "idle";
        state.error = null;
        // toast.success("Logged out successfully");
      })
      .addCase(logout.rejected, (state, action) => {
        // Now TypeScript knows action.payload has a message property
        state.error = action.payload?.message || "Logout failed";
      })

      // Register student cases
      .addCase(registerStudent.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(registerStudent.fulfilled, (state, action) => {
        state.status = "succeeded";
        const studentData = action.payload.user;
        state.user = {
          ...studentData,
          accessToken: studentData.accessToken,
          refreshToken: studentData.refreshToken,
        };
        state.isAuthenticated = true;
        state.error = null;
        //toast.success("Registered successfully");
      })
      .addCase(registerStudent.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Registration failed";
        state.isAuthenticated = false;
      });
  },
});

// Export actions
export const {
  resetAuthStatus,
  resetAuthData,
  addToOfflineQueue,
  removeFromOfflineQueue,
} = authSlice.actions;

// Export selectors
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated;
export const selectAuthStatus = (state: { auth: AuthState }) =>
  state.auth.status;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;

export default authSlice.reducer;
